// ── The Tracker timeline (ADO-544 fetch layer, extended for ADO-545) ──
// One flat, chronological list of developments across every tracked domain.
// The spine pages backwards through history with per-source keyset cursors and
// server-side alarm predicates so the default view (alarm 4+) never bulk-fetches
// rows it won't show (egress rule, CLAUDE.md #11).

import { SEVERITY_TO_ALARM } from './adapter';

export type TimelineSource = 'stories' | 'eos' | 'scotus' | 'pardons';

/** The Tracker's scope: term 2 only. Bounds paging AND the tally counts. */
export const TERM_START = '2025-01-20';

/** The alarm segmented filter's positions: All / 3+ / 4+ / Only 5 */
export type AlarmMin = 0 | 3 | 4 | 5;

/**
 * The Tracker's view selector (ADO-554): 'main' is the curated main line
 * (rule v2 in v_tracker_main_line_rule, PRD §14.4 — the default), the numbers are raw alarm floors
 * that recover the complete record.
 */
export type TrackerView = AlarmMin | 'main';

/**
 * What a row's news is (ADO-594, PRD §14.2): something he did, something he
 * said, or reporting about it (`coverage`, public word "Analysis").
 */
export type ActionKind = 'did' | 'said' | 'coverage';

/** Chip order and public labels for the Did/Said/Analysis chips (PRD §14.5, did_said flag) */
export const ACTION_KINDS: ActionKind[] = ['did', 'said', 'coverage'];
export const ACTION_KIND_LABELS: Record<ActionKind, string> = {
  did: 'Did',
  said: 'Said',
  coverage: 'Analysis',
};

const isActionKind = (v: unknown): v is ActionKind =>
  typeof v === 'string' && (ACTION_KINDS as string[]).includes(v);

export interface TimelineEntry {
  id: string | number;
  source: TimelineSource;
  /** ISO date the development happened */
  date: string;
  headline: string;
  alarm: number;
  /** Published front this entry belongs to (stories only, via v_tracker_stories) */
  front?: { name: string; slug: string };
  /** stories.action_label (stories only, did_said flag); absent = not labeled yet */
  label?: ActionKind;
  /** The case name under a plain-English SCOTUS headline (scotus only); absent when it IS the headline */
  caseName?: string;
}

/**
 * The chip a row answers to (PRD §14.5 table): a story by its label, an
 * unlabeled story and every EO, SCOTUS ruling and pardon by Did.
 */
export function entryKind(e: TimelineEntry): ActionKind {
  return e.source === 'stories' && e.label ? e.label : 'did';
}

export const SOURCE_LABELS: Record<TimelineSource, string> = {
  stories: 'Stories',
  scotus: 'Supreme Court',
  eos: 'Executive Orders',
  pardons: 'Pardons',
};

/** Per-entry type label on the spine (what a row IS: story, EO, ruling, pardon) */
export const ENTRY_TYPE_LABELS: Record<TimelineSource, string> = {
  stories: 'Story',
  scotus: 'Supreme Court',
  eos: 'EO',
  pardons: 'Pardon',
};

/** Route prefix for each source's detail page */
export const SOURCE_ROUTES: Record<TimelineSource, string> = {
  stories: 'detail',
  eos: 'eos',
  scotus: 'scotus',
  pardons: 'pardons',
};

function clampAlarm(n: unknown, fallback: number): number {
  // Number(null) is 0, not NaN — nulls must take the fallback, or every
  // null-alarm EO/SCOTUS row reads as alarm 0 and the client filter drops
  // rows the server's null-fallback predicate deliberately included
  if (n == null) return fallback;
  const v = Number(n);
  return Number.isFinite(v) ? Math.max(0, Math.min(5, v)) : fallback;
}

type Raw = Record<string, unknown>;

export function storyRowToEntry(raw: Raw): TimelineEntry {
  const severity = raw.severity as string | null;
  const alarm = raw.alarm_level != null
    ? clampAlarm(raw.alarm_level, 2)
    : (severity && SEVERITY_TO_ALARM[severity] != null ? SEVERITY_TO_ALARM[severity] : 2);
  return {
    id: raw.id as number,
    source: 'stories',
    date: (raw.first_seen_at as string) || '',
    headline: (raw.primary_headline as string) || '',
    alarm,
    front: raw.front_name
      ? { name: raw.front_name as string, slug: (raw.front_slug as string) || '' }
      : undefined,
    // Only selected with the did_said flag on; an unknown value reads as unlabeled
    ...(isActionKind(raw.action_label) ? { label: raw.action_label } : {}),
  };
}

export function eoRowToEntry(raw: Raw): TimelineEntry {
  return {
    id: raw.id as string | number,
    source: 'eos',
    date: (raw.date as string) || '',
    headline: (raw.title as string) || '',
    alarm: clampAlarm(raw.alarm_level, 3),
  };
}

/**
 * A ruling reads by what it did (ADO-609): the agent's plain-English
 * media_says headline, else the case name. Not ruling_label: older rows hold
 * an impact-scale tag ("Institutional Sabotage") or a bare disposition
 * ("Certiorari dismissed as improvidently granted") there, both worse than
 * the case name. With a media_says headline the case name rides underneath.
 */
export function scotusRowToEntry(raw: Raw): TimelineEntry {
  const text = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
  const caseName = text(raw.case_name) || text(raw.case_name_short);
  const headline = text(raw.media_says) || caseName;
  return {
    id: raw.id as number,
    source: 'scotus',
    date: (raw.decided_at as string) || '',
    headline,
    alarm: clampAlarm(raw.ruling_impact_level, 3),
    ...(caseName && caseName !== headline ? { caseName } : {}),
  };
}

export function pardonRowToEntry(raw: Raw): TimelineEntry {
  const name = (raw.recipient_name as string) || '';
  const nickname = raw.nickname as string | null;
  return {
    id: raw.id as number,
    source: 'pardons',
    date: (raw.pardon_date as string) || '',
    headline: nickname ? `Pardoned: ${name} ("${nickname}")` : `Pardoned: ${name}`,
    alarm: clampAlarm(raw.corruption_level, 2),
  };
}

/**
 * Merge per-source rows into one ascending chronological list, dropping
 * undated rows and keeping one entry per source + id (a backstop: the same
 * row twice would render twice under one React key).
 */
export function mergeEntries(groups: TimelineEntry[][]): TimelineEntry[] {
  const seen = new Set<string>();
  return groups
    .flat()
    .filter(e => {
      if (!e.date || !e.headline) return false;
      const key = `${e.source}:${e.id}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => a.date.localeCompare(b.date) || String(a.id).localeCompare(String(b.id)));
}

// ── Per-source query specs ──

interface SourceSpec {
  table: string;
  select: string;
  /** Base row filters that always apply (published/public predicates) */
  base: string;
  dateCol: string;
  limit: number;
  adapter: (raw: Raw) => TimelineEntry;
  /**
   * Alarm predicate in PostgREST logic-tree syntax (goes inside `and=(...)`),
   * or null for "no filter". Null alarm columns follow each adapter's default:
   * stories fall back to severity, EOs/SCOTUS default to 3 (so nulls pass a
   * 3+ filter but not 4+), pardons default to 2 (nulls only pass "All").
   */
  alarm: (min: AlarmMin) => string | null;
}

// Tight selects on purpose — never widen these to * (egress rule, CLAUDE.md #11).
// Key order is the chip display order (mockup rev 6).
const SPECS: Record<TimelineSource, SourceSpec> = {
  stories: {
    // v_tracker_stories (migration 112, rewritten in 113) bakes in status=active
    // + enriched and adds front membership + the main_line column (ADO-554) —
    // a STORED flag since ADO-570, so main_line=is.true is an index-only scan.
    table: 'v_tracker_stories',
    select: 'id,primary_headline,first_seen_at,alarm_level,severity,front_name,front_slug',
    base: `first_seen_at=gte.${TERM_START}`,
    dateCol: 'first_seen_at',
    limit: 60,
    adapter: storyRowToEntry,
    alarm: min => {
      if (min === 0) return null;
      // Derived from the canonical adapter map so the server predicate can
      // never drift from the client-side severity fallback (Codex P1 on PR #126)
      const severities = Object.keys(SEVERITY_TO_ALARM)
        .filter(s => SEVERITY_TO_ALARM[s] >= min);
      return `or(alarm_level.gte.${min},and(alarm_level.is.null,severity.in.(${severities.join(',')})))`;
    },
  },
  scotus: {
    table: 'scotus_cases',
    select: 'id,case_name,case_name_short,media_says,decided_at,ruling_impact_level',
    base: `is_public=eq.true&decided_at=not.is.null&decided_at=gte.${TERM_START}`,
    dateCol: 'decided_at',
    limit: 25,
    adapter: scotusRowToEntry,
    alarm: min => {
      if (min === 0) return null;
      if (min === 3) return 'or(ruling_impact_level.gte.3,ruling_impact_level.is.null)';
      return `ruling_impact_level.gte.${min}`;
    },
  },
  eos: {
    table: 'executive_orders',
    select: 'id,title,date,alarm_level',
    base: `is_public=eq.true&date=gte.${TERM_START}`,
    dateCol: 'date',
    limit: 25,
    adapter: eoRowToEntry,
    alarm: min => {
      if (min === 0) return null;
      if (min === 3) return 'or(alarm_level.gte.3,alarm_level.is.null)';
      return `alarm_level.gte.${min}`;
    },
  },
  pardons: {
    table: 'pardons',
    select: 'id,recipient_name,nickname,pardon_date,corruption_level',
    base: `is_public=eq.true&pardon_date=gte.${TERM_START}`,
    dateCol: 'pardon_date',
    limit: 25,
    adapter: pardonRowToEntry,
    alarm: min => (min === 0 ? null : `corruption_level.gte.${min}`),
  },
};

export const TIMELINE_SOURCES = Object.keys(SPECS) as TimelineSource[];

// ── Keyset cursor paging ──

export interface SourceCursor {
  /** Raw date-column value of the oldest row fetched so far */
  date: string;
  id: string | number;
}

export interface SourceState {
  cursor: SourceCursor | null;
  /** No more rows (or the source errored — degrade to "done", never block the rest) */
  exhausted: boolean;
  /** Last fetch failed; used to hide the surface only when everything is down */
  errored: boolean;
  /**
   * Stories only, did_said flag on: the Did/Said/Analysis filter (labelKey)
   * this cursor was paged under. A cursor is never paged under another filter.
   */
  labels?: string;
  /**
   * Stories only: the view has no action_label column yet (before migration
   * 123), so the stream is paged without it and every story reads as Did.
   */
  noLabels?: boolean;
}

export type TrackerState = Record<TimelineSource, SourceState>;

const quoted = (v: string | number) => `"${String(v).replace(/"/g, '')}"`;

/** PostgREST's "column does not exist" (42703): the view predates migration 123. */
async function missingColumn(res: Response): Promise<boolean> {
  try {
    const body = await res.json();
    return (body as { code?: unknown } | null)?.code === '42703';
  } catch {
    return false;
  }
}

// ── Did / Said / Analysis chips (ADO-594 S6, PRD §14.5, did_said flag) ──
// Callers pass the set of kinds switched OFF, or undefined with the flag off,
// which leaves every query and state exactly as before.

/** Names the stories stream a set of switched-off kinds pages (the kinds on, in chip order). */
export function labelKey(kindsOff: ReadonlySet<ActionKind>): string {
  return ACTION_KINDS.filter(k => !kindsOff.has(k)).join(',') || 'none';
}

/**
 * The stories filter for the kinds switched on, in PostgREST logic-tree
 * syntax, or null with every kind on. Did covers unlabeled stories too, and
 * NULL never matches `in.()`, so Did adds an explicit `is.null`.
 */
export function storyLabelPredicate(kindsOff: ReadonlySet<ActionKind>): string | null {
  const on = ACTION_KINDS.filter(k => !kindsOff.has(k));
  if (on.length === ACTION_KINDS.length) return null;
  // Every kind off: stories are never fetched (effectiveOff); match nothing if they are
  if (on.length === 0) return 'id.is.null';
  const list = `action_label.in.(${on.join(',')})`;
  return on.includes('did') ? `or(action_label.is.null,${list})` : list;
}

/**
 * The sources not to fetch or count: the switched-off source chips, plus EOs,
 * SCOTUS rulings and pardons while Did is off (they are all actions), plus
 * stories while every kind is off. With no kind off it is `off` itself.
 */
export function effectiveOff(
  off: ReadonlySet<TimelineSource>,
  kindsOff?: ReadonlySet<ActionKind>,
): ReadonlySet<TimelineSource> {
  if (!kindsOff || kindsOff.size === 0) return off;
  const out = new Set(off);
  if (kindsOff.has('did')) for (const s of TIMELINE_SOURCES) if (s !== 'stories') out.add(s);
  if (ACTION_KINDS.every(k => kindsOff.has(k))) out.add('stories');
  return out;
}

/**
 * The state with the stories stream reset to its first page when it was
 * paged under a different Did/Said/Analysis filter, so a switched kind is
 * paged afresh (and caught up like a chip switched back on) instead of being
 * mixed into the old cursor. Returns `state` itself when nothing changes.
 */
export function alignStoryLabels(state: TrackerState, kindsOff?: ReadonlySet<ActionKind>): TrackerState {
  if (!kindsOff) return state;
  const key = labelKey(kindsOff);
  const st = state.stories;
  if (st.labels === key) return state;
  return {
    ...state,
    stories: { cursor: null, exhausted: false, errored: false, labels: key, ...(st.noLabels ? { noLabels: true } : {}) },
  };
}

/**
 * The empty-list message while Did is off (PRD §14.5 copy), or null.
 * Public copy never says "coverage" or "story".
 */
export function kindsEmptyMessage(kindsOff?: ReadonlySet<ActionKind>): string | null {
  if (!kindsOff?.has('did')) return null;
  if (kindsOff.has('said') && kindsOff.has('coverage')) return 'Did, Said and Analysis are all switched off · switch one back on.';
  if (!kindsOff.has('said') && kindsOff.has('coverage')) return 'Nothing said in this range. Turn Did back on to see what he did.';
  return 'Nothing here at this filter. Turn Did back on to see what he did.';
}

/**
 * Main-line bar for EOs, SCOTUS rulings and pardons: they are actions by
 * definition, so they need no front to qualify. Lowered from 5 to 4 on
 * October 3, 2026 (ADO-594 D5, shipped with the alarm 4-5 homepage, ADO-608).
 */
export const MAIN_LINE_SOURCE_ALARM = 4;

/**
 * Build the PostgREST path for one source page. Pure — unit-tested.
 * Keyset pagination: order date desc, id desc; the next page is
 * (date < D) OR (date = D AND id < I). Values are quoted so timestamps
 * with `:`/`+` survive, and the whole logic tree is URL-encoded.
 *
 * In the 'main' view (ADO-554) stories filter on the server-computed
 * main_line column; the other sources get MAIN_LINE_SOURCE_ALARM, with pins
 * applied client-side.
 *
 * `kindsOff` (did_said flag only) adds action_label to the stories select and
 * filters stories by the Did/Said/Analysis chips; other sources ignore it.
 * Undefined leaves the path exactly as it was before the flag.
 */
export function buildSourcePath(
  source: TimelineSource,
  view: TrackerView,
  cursor: SourceCursor | null,
  kindsOff?: ReadonlySet<ActionKind>,
): string {
  const s = SPECS[source];
  const labelled = source === 'stories' && kindsOff !== undefined;
  const conditions: string[] = [];
  const alarmFrag = view === 'main'
    ? (source === 'stories' ? 'main_line.is.true' : s.alarm(MAIN_LINE_SOURCE_ALARM))
    : s.alarm(view);
  if (alarmFrag) conditions.push(alarmFrag);
  const labelFrag = labelled ? storyLabelPredicate(kindsOff) : null;
  if (labelFrag) conditions.push(labelFrag);
  if (cursor) {
    conditions.push(
      `or(${s.dateCol}.lt.${quoted(cursor.date)},`
      + `and(${s.dateCol}.eq.${quoted(cursor.date)},id.lt.${quoted(cursor.id)}))`,
    );
  }
  const logic = conditions.length ? `&and=${encodeURIComponent(`(${conditions.join(',')})`)}` : '';
  return `${s.table}?select=${s.select}${labelled ? ',action_label' : ''}&${s.base}${logic}`
    + `&order=${s.dateCol}.desc,id.desc&limit=${s.limit}`;
}

export function initialTrackerState(): TrackerState {
  const state = {} as TrackerState;
  for (const src of TIMELINE_SOURCES) {
    state[src] = { cursor: null, exhausted: false, errored: false };
  }
  return state;
}

// ── Tracker pins (ADO-554) ──

/** Map keyed `${source}:${entity_id}` → pin. Tiny table, fetched whole. */
export type TrackerPins = Map<string, 'force_show' | 'force_hide'>;

export const pinKey = (source: TimelineSource, id: string | number) => `${source}:${id}`;

/**
 * Fetch every pin. Failure (including the table not existing yet — code can
 * deploy ahead of migration 112 while the flag is off) degrades to no pins.
 */
export async function fetchTrackerPins(signal?: AbortSignal): Promise<TrackerPins> {
  const pins: TrackerPins = new Map();
  try {
    const { url, anonKey } = await import('./supabase');
    // Newest-first so if curation ever outgrows the cap, the truncation is
    // deterministic and the most recent pins win (review note on PR #127).
    const res = await fetch(
      `${url}/rest/v1/tracker_pin?select=source,entity_id,pin&order=updated_at.desc&limit=1000`,
      { headers: { 'apikey': anonKey, 'Authorization': `Bearer ${anonKey}` }, signal },
    );
    if (!res.ok) return pins;
    const rows: Raw[] = await res.json();
    for (const r of rows) {
      pins.set(pinKey(r.source as TimelineSource, r.entity_id as string), r.pin as 'force_show' | 'force_hide');
    }
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
  }
  return pins;
}

/**
 * The force_show pins for non-stories sources, grouped per source. Stories
 * pins are resolved server-side in v_tracker_stories, so they are excluded.
 */
export function forceShowIdsBySource(pins: TrackerPins): Partial<Record<TimelineSource, string[]>> {
  const out: Partial<Record<TimelineSource, string[]>> = {};
  for (const [key, pin] of pins) {
    if (pin !== 'force_show') continue;
    const sep = key.indexOf(':');
    const source = key.slice(0, sep) as TimelineSource;
    if (source === 'stories' || !TIMELINE_SOURCES.includes(source)) continue;
    (out[source] ??= []).push(key.slice(sep + 1));
  }
  return out;
}

/**
 * Fetch the next page for every non-exhausted source and advance its cursor.
 * Returns only the NEW entries (ascending); callers merge with what they hold.
 *
 * Sources in `off` (switched-off chips, ADO-593) are not fetched at all and
 * keep their state, so switching the chip back on resumes where it stopped,
 * or starts at its first page if it was never fetched.
 *
 * In the 'main' view, pins adjust the non-stories sources client-side:
 * force_hide entries are dropped from every page, and on a source's FIRST
 * page (no cursor yet) force_show rows below the main-line bar are fetched
 * by id and merged in at their chronological position. Stories pins are
 * already applied by v_tracker_stories on the server.
 *
 * Pinned rows surface AT THEIR DATE, deliberately: the Tracker is a
 * chronological record, not a pinboard (pin-to-top is the ADO-552 hero
 * carousel), so an injected old pin stays buffered by the coverage frontier
 * until paging reaches its date — exempting it would fake completeness of a
 * range that hasn't loaded. Covered by the frontier test in the pins suite.
 *
 * `kindsOff` (did_said flag only, ADO-594 S6): the Did/Said/Analysis chips
 * switched off. Stories are filtered by label on the server and paged per
 * filter (alignStoryLabels); with Did off, EOs, SCOTUS and pardons are not
 * fetched (effectiveOff). Against a view without the label column (before
 * migration 123) stories fall back to the plain query, where every story is
 * unlabeled and so Did.
 */
export async function fetchTrackerPage(
  view: TrackerView,
  state: TrackerState | null,
  signal?: AbortSignal,
  // May be a promise so callers can fetch pins CONCURRENTLY with the source
  // pages (pins are only consumed after every page response has arrived).
  pins?: TrackerPins | Promise<TrackerPins | undefined>,
  sourcesOff: ReadonlySet<TimelineSource> = new Set(),
  kindsOff?: ReadonlySet<ActionKind>,
): Promise<{ entries: TimelineEntry[]; state: TrackerState }> {
  const off = effectiveOff(sourcesOff, kindsOff);
  // Lazy import: lib/supabase reads window.location at module load, which would
  // break node-env unit tests that import this module's pure functions.
  const { url, anonKey } = await import('./supabase');
  const headers = {
    'apikey': anonKey,
    'Authorization': `Bearer ${anonKey}`,
  };

  const prev = alignStoryLabels(state ?? initialTrackerState(), kindsOff);
  const next: TrackerState = { ...prev };
  const isMain = view === 'main';
  const firstPage = new Set<TimelineSource>();

  // Main line: force_show rows below the main-line bar are fetched by id for
  // every source attempting its FIRST page (the first load, or a chip switched
  // back on after a load that skipped it). The lookup needs only the pins, so
  // it runs concurrently with the source pages instead of after them (ADO-605:
  // it was a serial round trip before first paint). Its rows are used only
  // for sources whose first page then succeeds; anything at the bar arrives through
  // normal paging, so injecting it again would duplicate the entry.
  const firstAttempt = TIMELINE_SOURCES.filter(s => !prev[s].exhausted && !prev[s].cursor && !off.has(s));
  const injectedP: Promise<Partial<Record<TimelineSource, TimelineEntry[]>>> = !isMain || !pins || !firstAttempt.length
    ? Promise.resolve({})
    : Promise.resolve(pins).then(async resolved => {
      if (!resolved?.size) return {};
      const bySource = forceShowIdsBySource(resolved);
      const out: Partial<Record<TimelineSource, TimelineEntry[]>> = {};
      await Promise.all(
        (Object.keys(bySource) as TimelineSource[]).filter(s => firstAttempt.includes(s)).map(async source => {
          const spec = SPECS[source];
          const ids = bySource[source]!.map(id => quoted(id)).join(',');
          try {
            const res = await fetch(
              `${url}/rest/v1/${spec.table}?select=${spec.select}&${spec.base}&id=in.(${ids})`,
              { headers, signal },
            );
            if (!res.ok) return;
            const rows: Raw[] = await res.json();
            out[source] = rows.map(spec.adapter).filter(e => e.alarm < MAIN_LINE_SOURCE_ALARM);
          } catch (err) {
            if ((err as Error).name === 'AbortError') throw err;
            // pins are additive — a failed fetch must not break the page
          }
        }),
      );
      return out;
    });
  // Awaited below; if a page fetch throws first (abort), this must not surface
  // as an unhandled rejection.
  injectedP.catch(() => {});

  const groups = await Promise.all(
    TIMELINE_SOURCES.map(async source => {
      const st = prev[source];
      if (st.exhausted || off.has(source)) return [];
      const spec = SPECS[source];
      // Only stories carry labels, and only with the did_said flag on
      const labelled = source === 'stories' && kindsOff !== undefined;
      let noLabels = labelled && st.noLabels === true;
      // Without the label column every story is unlabeled, so Did: Did off matches none
      const noneMatch = () => {
        next[source] = { ...st, exhausted: true, errored: false, noLabels: true };
        return [];
      };
      if (noLabels && kindsOff!.has('did')) return noneMatch();
      try {
        const pathFor = (withLabels: boolean) =>
          `${url}/rest/v1/${buildSourcePath(source, view, st.cursor, withLabels ? kindsOff : undefined)}`;
        let res = await fetch(pathFor(labelled && !noLabels), { headers, signal });
        if (labelled && !noLabels && res.status === 400 && await missingColumn(res)) {
          noLabels = true;
          if (kindsOff!.has('did')) return noneMatch();
          res = await fetch(pathFor(false), { headers, signal });
        }
        if (!res.ok) {
          next[source] = { ...st, exhausted: true, errored: true };
          return [];
        }
        const rows: Raw[] = await res.json();
        // Pins inject only once a first page SUCCEEDS: a failed one is retried
        // from no cursor, and injecting on both attempts would duplicate them
        if (!st.cursor) firstPage.add(source);
        const last = rows[rows.length - 1];
        next[source] = {
          cursor: last
            ? { date: (last[spec.dateCol] as string) || '', id: last.id as string | number }
            : st.cursor,
          exhausted: rows.length < spec.limit,
          errored: false,
          ...(labelled ? { labels: labelKey(kindsOff!) } : {}),
          ...(noLabels ? { noLabels: true } : {}),
        };
        return rows.map(spec.adapter);
      } catch (err) {
        if ((err as Error).name === 'AbortError') throw err;
        // One source failing must not blank the whole Tracker
        next[source] = { ...st, exhausted: true, errored: true };
        return [];
      }
    }),
  );

  // Pins are applied only now that every source page has arrived, so a
  // promise-valued `pins` never delays the fetches themselves.
  const resolvedPins = await pins;
  if (isMain && resolvedPins?.size) {
    TIMELINE_SOURCES.forEach((source, i) => {
      if (source === 'stories') return;
      groups[i] = groups[i].filter(e => resolvedPins.get(pinKey(source, e.id)) !== 'force_hide');
    });
  }

  // Surface the force_show rows only for sources whose first page succeeded:
  // a failed one is retried from no cursor, and injecting on both attempts
  // would duplicate them
  const injected = await injectedP;
  for (const source of firstPage) {
    const rows = injected[source];
    if (rows) groups.push(rows);
  }

  return { entries: mergeEntries(groups), state: next };
}

/**
 * The oldest date at which coverage is COMPLETE across all sources: the max
 * cursor date among sources that still have unfetched rows. Entries older than
 * this are buffered, not shown — otherwise a sparse source (25 EOs reach back
 * months, 60 stories reach back days) would fake gaps in the record.
 * Sources in `off` are skipped (ADO-593): a switched-off chip must not hold
 * back the sources still on. A source that is on but not fetched yet (a chip
 * switched back on, waiting for its first page) covers nothing yet, so the
 * frontier is FRONTIER_PENDING; callers keep their previous frontier for that
 * round trip (holdFrontier). Null means every source still counted is
 * exhausted: show everything.
 */
export function coverageFrontier(
  state: TrackerState,
  off: ReadonlySet<TimelineSource> = new Set(),
): string | null {
  let frontier: string | null = null;
  for (const src of TIMELINE_SOURCES) {
    const st = state[src];
    if (st.exhausted || off.has(src)) continue;
    if (!st.cursor) return FRONTIER_PENDING;
    if (frontier === null || st.cursor.date > frontier) frontier = st.cursor.date;
  }
  return frontier;
}

/** coverageFrontier's answer while a switched-on source has no first page yet. */
export const FRONTIER_PENDING = '\uffff';

/**
 * The frontier to render. While a switched-on source is pending, keep the one
 * already on screen (`held`) so the list neither collapses nor moves under
 * the reader's scroll, and recompute once its first page lands. With nothing
 * held yet (a chip switched on mid-load, or every source on was exhausted),
 * hold at the coverage frontier of the sources on that have loaded (the same
 * rule, with the pending ones left out). Never null, which would show every
 * buffered row with fake gaps and then pull them back: if those sources have
 * no frontier either, show nothing until the first page lands.
 */
export function holdFrontier(
  state: TrackerState,
  off: ReadonlySet<TimelineSource>,
  held: string | null,
): string | null {
  const next = coverageFrontier(state, off);
  if (next !== FRONTIER_PENDING) return next;
  if (held !== null) return held;
  const pending = TIMELINE_SOURCES.filter(s => !off.has(s) && !state[s].exhausted && !state[s].cursor);
  return coverageFrontier(state, new Set([...off, ...pending])) ?? FRONTIER_PENDING;
}

/** The older of two frontiers. Null (show everything) is the oldest of all. */
export function olderFrontier(a: string | null, b: string | null): string | null {
  if (a === null || b === null) return null;
  return a < b ? a : b;
}

/**
 * The frontier to DISPLAY. Within a view it never moves newer: a chip
 * switched back on, a catch-up stopped at its cap, or a retry from an old
 * cursor can push the computed frontier forward, which would pull rows out
 * from under the reader, so the older of `prev` and the computed one wins.
 * `prev` undefined means nothing displayed yet for this view (a view change
 * resets it), which starts from holdFrontier's never-null rule.
 *
 * Null ("show everything") is only real when switched-on sources have
 * loaded everything. A null that just means no switched-on source can say
 * anything (every chip off, or every source on failed) keeps what is on
 * screen instead, and rememberFrontier does not store it, so it never
 * sticks for the rest of the view.
 */
export function displayedFrontier(
  state: TrackerState,
  off: ReadonlySet<TimelineSource>,
  prev: string | null | undefined,
): string | null {
  const computed = prev === undefined ? holdFrontier(state, off, null) : coverageFrontier(state, off);
  if (computed === null && !onSourcesConstrain(state, off)) return prev ?? null;
  return prev === undefined ? computed : olderFrontier(prev, computed);
}

/** The value to keep as `prev` for the next render (see displayedFrontier). */
export function rememberFrontier(
  state: TrackerState,
  off: ReadonlySet<TimelineSource>,
  prev: string | null | undefined,
  shown: string | null,
): string | null | undefined {
  return shown === null && !onSourcesConstrain(state, off) ? prev : shown;
}

/** Some switched-on source has rows and has not failed, so it can bound coverage. */
function onSourcesConstrain(state: TrackerState, off: ReadonlySet<TimelineSource>): boolean {
  return TIMELINE_SOURCES.some(s => !off.has(s) && !state[s].errored && state[s].cursor !== null);
}

/**
 * Switched-on sources still short of the displayed frontier (never fetched,
 * or a cursor newer than it). "Load earlier" pages these alone first, and the
 * count line says "Updating…" while there are any.
 */
export function behindSources(
  state: TrackerState,
  off: ReadonlySet<TimelineSource>,
  displayed: string | null,
): TimelineSource[] {
  return TIMELINE_SOURCES.filter(s => {
    const st = state[s];
    if (off.has(s) || st.exhausted) return false;
    return !st.cursor || displayed === null || st.cursor.date > displayed;
  });
}

/**
 * What the count line and the "load earlier" button say. "Updating…" only
 * while something is actually loading for the view or for a source that is
 * behind; when idle but behind, the button says which sources it will catch
 * up, so the reader knows why the record is not complete yet. `busy` (any
 * fetch in flight) hides the empty-list message; being behind while idle
 * does not.
 */
export function trackerProgress(o: {
  refreshing: boolean;
  loadingMore: boolean;
  failed: boolean;
  behind: readonly TimelineSource[];
}): { updating: boolean; busy: boolean; button: string } {
  const updating = o.refreshing || (o.loadingMore && o.behind.length > 0);
  const busy = o.refreshing || o.loadingMore;
  const button = o.loadingMore
    ? 'Loading earlier…'
    : o.failed
      ? 'Try again · load earlier ↓'
      : o.behind.length > 0
        ? `Load earlier · catching up ${o.behind.map(s => SOURCE_LABELS[s]).join(', ')} ↓`
        : 'Keep going · load earlier ↓';
  return { updating, busy, button };
}

/**
 * What the count line says after the number. While a source is behind (a
 * capped catch-up, or a chip switched on with everything showing), the list
 * is missing part of that source, so it is never "the complete record"
 * (Codex P1 on PR #158).
 */
export function countScope(view: TrackerView, behind: readonly TimelineSource[]): string {
  const scope = view === 'main' ? ' · the main line' : view > 0 ? ` at alarm ${view}+` : '';
  if (behind.length > 0) return `${scope} · catching up ${behind.map(s => SOURCE_LABELS[s]).join(', ')}`;
  return scope || ' · the complete record';
}

/** Where a source that is behind stops covering the record (see coverageGaps). */
export interface CoverageGap {
  source: TimelineSource;
  /** The oldest date the source has reached; null = nothing loaded yet */
  from: string | null;
  /** Position in the visible list (newest first) the marker goes before */
  index: number;
}

/**
 * A source that is behind shows its recent rows, then nothing from it down to
 * the frontier while the other sources carry on (a catch-up stopped at its
 * cap, a chip switched on with everything showing). The spine marks where each
 * one stops, so that stretch never passes as one continuous record (Codex P1
 * on PR #158). `visible` is newest first, as visibleEntries returns it.
 */
export function coverageGaps(
  visible: readonly TimelineEntry[],
  state: TrackerState,
  behind: readonly TimelineSource[],
): CoverageGap[] {
  return behind.map(source => {
    const from = state[source].cursor?.date ?? null;
    if (from === null) return { source, from, index: 0 };
    const i = visible.findIndex(e => e.date < from);
    return { source, from, index: i === -1 ? visible.length : i };
  });
}

/** Most pages one catch-up fetches before handing over to "load earlier". */
export const CATCH_UP_MAX_PAGES = 10;

/**
 * Catch-up for a chip switched back on (ADO-593): page ONLY `source` until its
 * cursor reaches `target` (the frontier on screen when the chip went on), it
 * runs out or fails, or CATCH_UP_MAX_PAGES pages are fetched. The caller holds
 * the frontier at `target` meanwhile, so no row on screen disappears. A null
 * target (everything was showing) needs no catch-up: nothing is fetched,
 * and "load earlier" catches the source up instead. `stillOn`
 * is checked before every page so switching the chip off again stops it; an
 * aborted `signal` (view change) rejects with AbortError.
 */
export async function catchUpSource(
  view: TrackerView,
  state: TrackerState,
  source: TimelineSource,
  target: string | null,
  opts: {
    signal?: AbortSignal;
    pins?: TrackerPins;
    maxPages?: number;
    stillOn?: () => boolean;
    /** did_said flag only: the Did/Said/Analysis chips switched off */
    kindsOff?: ReadonlySet<ActionKind>;
  } = {},
): Promise<{ entries: TimelineEntry[]; state: TrackerState; pages: number }> {
  const others = new Set(TIMELINE_SOURCES.filter(s => s !== source));
  const maxPages = opts.maxPages ?? CATCH_UP_MAX_PAGES;
  const groups: TimelineEntry[][] = [];
  let current = alignStoryLabels(state, opts.kindsOff);
  let pages = 0;
  while (target !== null && pages < maxPages) {
    const st = current[source];
    if (st.exhausted) break;
    if (st.cursor && st.cursor.date <= target) break;
    if (opts.stillOn && !opts.stillOn()) break;
    const next = await fetchTrackerPage(view, current, opts.signal, opts.pins, others, opts.kindsOff);
    groups.push(next.entries);
    current = next.state;
    pages++;
  }
  return { entries: mergeEntries(groups), state: current, pages };
}

/** Every switched-on source has nothing left to load (true when every chip is off). */
export function allOnExhausted(state: TrackerState, off: ReadonlySet<TimelineSource>): boolean {
  return TIMELINE_SOURCES.every(s => off.has(s) || state[s].exhausted);
}

/**
 * Some switched-on source failed. A failed request marks the source exhausted
 * so it never blocks the rest, but it is owed a retry: while this holds, the
 * record is not "the whole record" (Codex P1 on PR #158).
 */
export function anyOnErrored(state: TrackerState, off: ReadonlySet<TimelineSource>): boolean {
  return TIMELINE_SOURCES.some(s => !off.has(s) && state[s].errored);
}

/**
 * The state to page from on a retry: every switched-on source that failed is
 * reopened at its last good cursor, so fetchTrackerPage refetches the page
 * that failed (or the first page, if that one failed). Healthy and
 * switched-off sources are untouched.
 */
export function retryErrored(state: TrackerState, off: ReadonlySet<TimelineSource>): TrackerState {
  const next: TrackerState = { ...state };
  for (const s of TIMELINE_SOURCES) {
    if (!off.has(s) && state[s].errored) next[s] = { ...state[s], exhausted: false, errored: false };
  }
  return next;
}

/**
 * Every switched-on source failed. Off sources are never fetched, so they never
 * error and must not count; every chip off is a choice, not an outage.
 */
export function allOnErrored(state: TrackerState, off: ReadonlySet<TimelineSource>): boolean {
  return TIMELINE_SOURCES.some(s => !off.has(s))
    && TIMELINE_SOURCES.every(s => off.has(s) || state[s].errored);
}

export interface VisibleOptions {
  frontier: string | null;
  min: AlarmMin;
  off: ReadonlySet<TimelineSource>;
  query: string;
  /**
   * did_said flag only: Did/Said/Analysis chips switched off. The server
   * already filters, so this only hides rows held from before a chip changed.
   */
  kindsOff?: ReadonlySet<ActionKind>;
}

/** Apply the combined client-side filters. Returns newest-first (render order). */
export function visibleEntries(entries: TimelineEntry[], opts: VisibleOptions): TimelineEntry[] {
  const q = opts.query.trim().toLowerCase();
  const out = entries.filter(e =>
    !opts.off.has(e.source)
    && !opts.kindsOff?.has(entryKind(e))
    && e.alarm >= opts.min
    && (opts.frontier === null || e.date >= opts.frontier)
    && (!q || e.headline.toLowerCase().includes(q) || !!e.caseName?.toLowerCase().includes(q)),
  );
  out.reverse();
  return out;
}

// ── Tally (headline numbers above the spine) ──

export interface TrackerTally {
  /** Total published developments across all four sources */
  developments: number | null;
  /** Alarm-5 developments in the last 30 days */
  alarm5Last30: number | null;
  /** Published, unresolved fronts (ADO-554); null before migration 112 or on error */
  openFronts: number | null;
}

/**
 * One GET on the precomputed tracker_stats row (migration 113, ADO-570).
 * Replaces 9 HEAD count=exact requests, two of which scanned every active
 * story on PROD (2s cold) and hid the tile until all of them resolved.
 * The counts are as of refreshed_at (end of the last pipeline run). Any
 * failure or a missing row yields nulls, which hides the tiles - never throws
 * except on abort.
 */
export async function fetchTrackerTally(signal?: AbortSignal): Promise<TrackerTally> {
  const empty: TrackerTally = { developments: null, alarm5Last30: null, openFronts: null };
  const { url, anonKey } = await import('./supabase');
  try {
    const res = await fetch(
      `${url}/rest/v1/tracker_stats?select=developments,alarm5_last30,open_fronts&id=eq.1&limit=1`,
      { headers: { 'apikey': anonKey, 'Authorization': `Bearer ${anonKey}` }, signal },
    );
    if (!res.ok) return empty;
    const rows: Raw[] = await res.json();
    const row = rows[0];
    if (!row) return empty;
    const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
    return {
      developments: num(row.developments),
      alarm5Last30: num(row.alarm5_last30),
      openFronts: num(row.open_fronts),
    };
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    return empty;
  }
}
